import React, { useMemo } from 'react';

import { gql } from '@apollo/client';
import { formatDateRangeIntl, useFormatDateTime, useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { assertIsoDate, getLocalTimezone, getTZAbbr } from '@modernloop/shared/datetime';
import { AvailabilityIcon, EditIcon, WarningIcon } from '@modernloop/shared/icons';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { isEmpty } from 'lodash';

import {
  CandidateAvailability_AvailabilityFragment,
  CandidateAvailability_AvailabilityRequestFragment,
} from 'src/generated/mloop-graphql';

import useCompanyHolidays from 'src/hooks/useCompanyHolidays';

import { assertIsoTimestamp } from 'src/types/IsoTimestamp';

import { formatToTimeZone } from 'src/utils/datetime/Format';
import { getTimezoneDisplayName } from 'src/utils/datetime/Timezone';

import {
  getErrorText,
  getWarningText,
  hasWarningOrError,
} from 'src/views/booking/BookingCreateView/CandidateAvailabilityStep/candidateAvailabilityUtils';

import { CandidateAvailabilityByDate, getCandidateAvailabilityByDate } from './utils';

type Fragments = {
  availability: CandidateAvailability_AvailabilityFragment | undefined;
  availabilityRequest: CandidateAvailability_AvailabilityRequestFragment | undefined;
};

type Props = {
  candidateTimezone?: string;
  readonly?: boolean;
  showEditIcon?: boolean;
  onAddAvailability: () => void;
};

const CandidateAvailability: FCWithFragments<Fragments, Props> = ({
  availability,
  availabilityRequest,
  candidateTimezone,
  readonly,
  showEditIcon = true,
  onAddAvailability,
}) => {
  const { t } = useTranslation();
  const formatDate = useFormatDateTime();
  const holidays = useCompanyHolidays();

  const timezone = candidateTimezone || availabilityRequest?.timezone || getLocalTimezone();

  const availabilityByDate = useMemo((): CandidateAvailabilityByDate => {
    return getCandidateAvailabilityByDate({
      rcEnteredAvailability: availability?.rcEnteredAvailability || [],
      candidateEnteredAvailability: availability?.candidateEnteredAvailability || [],
      timezone,
    });
  }, [availability?.candidateEnteredAvailability, availability?.rcEnteredAvailability, timezone]);

  if (availabilityRequest && availabilityRequest.lastCandidateCommunicationSentAt && isEmpty(availabilityByDate)) {
    return (
      <Box marginLeft={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box display="flex" alignItems="center">
            <AvailabilityIcon />
          </Box>
          <Typography>{t('hiring-dashboard-candidates:candidates.availability.requested')}</Typography>
        </Stack>
      </Box>
    );
  }

  const availabilityPresent = Boolean(
    availability?.candidateEnteredAvailability?.length || availability?.rcEnteredAvailability?.length
  );

  return (
    <Paper>
      <Stack spacing={0.25}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Stack>
            {Object.keys(availabilityByDate).map((date) => {
              return (
                <Stack key={date} direction="row">
                  <Box paddingBottom={1} paddingTop={1} width="236px">
                    <Typography>{formatDate(assertIsoDate(date), 'MonthDayYearLong')}</Typography>
                  </Box>
                  <Box display="flex" flexDirection="column">
                    {availabilityByDate[date].map((a) => {
                      const warningOrError = hasWarningOrError(
                        { id: '', day: a.startAt, startAt: a.startAt, endAt: a.endAt },
                        timezone,
                        holidays
                      );

                      const errorText = warningOrError
                        ? getErrorText({
                            id: '',
                            day: a.startAt,
                            startAt: a.startAt,
                            endAt: a.endAt,
                          })
                        : '';

                      const warningText = warningOrError
                        ? getWarningText(
                            { id: '', day: a.startAt, startAt: a.startAt, endAt: a.endAt },
                            timezone,
                            holidays
                          )
                        : '';

                      // Intl.formatRange collapses the shared meridiem natively
                      // (e.g. "9:00 – 11:00 AM") and shows both when they differ.
                      const timeRangeLabel = formatDateRangeIntl(
                        assertIsoTimestamp(a.startAt),
                        assertIsoTimestamp(a.endAt),
                        'TimeShort',
                        {
                          timeZone: timezone,
                        }
                      );

                      return (
                        <Box key={timeRangeLabel} display="flex" alignItems="center" paddingBottom={1} paddingTop={1}>
                          <Typography color="text.secondary">{`${timeRangeLabel} ${getTZAbbr(timezone)}`}</Typography>
                          {(errorText || warningText) && (
                            <WarningIcon color={errorText ? 'error' : 'warning'} tooltip={errorText || warningText} />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Stack>
              );
            })}
          </Stack>
          {!readonly && availabilityPresent && showEditIcon && (
            <Button sx={{ flexShrink: 0 }} startIcon={<EditIcon />} onClick={onAddAvailability}>
              {t('hiring-dashboard-candidates:candidates.availability.edit')}
            </Button>
          )}
        </Stack>
        {availabilityPresent && availability?.updatedAt && (
          <Typography color="text.secondary" variant="body2">
            {t('hiring-dashboard-candidates:candidates.availability.lastUpdated', {
              date: formatToTimeZone(assertIsoTimestamp(availability.updatedAt), 'MMMM d, yyyy h:mm aaa', timezone),
            })}
          </Typography>
        )}
        {availabilityPresent && (
          <Typography color="text.secondary" variant="body2">
            {t('hiring-dashboard-candidates:candidates.availability.shownInCandidateTimezone', {
              timezone: getTimezoneDisplayName(candidateTimezone || 'UTC'),
            })}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

CandidateAvailability.fragments = {
  availability: gql`
    fragment CandidateAvailability_availability on CandidateAvailability {
      id
      rcEnteredAvailability {
        startAt
        endAt
      }
      candidateEnteredAvailability {
        startAt
        endAt
      }
      updatedAt
    }
  `,
  availabilityRequest: gql`
    fragment CandidateAvailability_availabilityRequest on CandidateAvailabilityOptions {
      id
      timezone
      lastCandidateCommunicationSentAt
    }
  `,
};

export default CandidateAvailability;
